import { MenuTypes } from '@ori-events/toggle-menu-flyout';
import { User, UserFilled } from '@ori-ui/icons';
import { ButtonBase, Link, styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import useTestId from '@ori/testid-generator';
import type { MouseEvent, VFC } from 'react';
import { useCallback } from 'react';

import { DESKTOP_BREAKPOINT } from '~/constants';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { dispatchToggleMenu } from '~/helpers';
import { useHover } from '~/hooks';
import { createMarginWithImportant } from '~/utils/createMarginWithImportant';

const UserIconRoot = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignItems: 'center',
    background: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    fontSize: theme.typography.pxToRem(20),
    height: 25,
    justifyContent: 'center',
    marginRight: createMarginWithImportant(theme, 6),
    width: 25,

    '&:hover, &:focus, &:active': {
      color: theme.palette.grey[600],
    },
  },

  [theme.breakpoints.up('lg')]: {
    marginRight: createMarginWithImportant(theme, 8),
  },
}));

const UserIconButton = styled(ButtonBase)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignItems: 'center',
    color: theme.palette.grey[600],
    display: 'flex',
    fontSize: theme.typography.pxToRem(28),
    marginRight: createMarginWithImportant(theme, 6),

    '&:hover, &:focus, &:active': {
      color: theme.palette.grey[600],
    },
  },

  [theme.breakpoints.up('lg')]: {
    marginRight: createMarginWithImportant(theme, 8),
  },
}));

export const UserIcon: VFC = () => {
  const [ref, Icon] = useHover(UserFilled, User);
  const { ssoAdLoginUrl } = useAppConfigurationContext();
  const { activeMenu } = useNavigationContext();
  const { isAnonymousUser } = useToken();

  const { getTestId } = useTestId();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if ((event.ctrlKey || event.metaKey) && ssoAdLoginUrl) {
        window.location.href = ssoAdLoginUrl;

        return;
      }

      dispatchToggleMenu(MenuTypes.AccountSideMenu, activeMenu);
    },
    [activeMenu, ssoAdLoginUrl],
  );

  if (!isAnonymousUser) {
    return (
      <UserIconRoot
        href="/system/ajax/login/home"
        data-testid={getTestId('user-icon')}
      >
        <UserFilled fontSize="inherit" />
      </UserIconRoot>
    );
  }

  return (
    <UserIconButton
      ref={ref}
      disableRipple
      data-testid={getTestId('user-icon')}
      onClick={handleClick}
    >
      <Icon fontSize="inherit" />
    </UserIconButton>
  );
};
